// Field catalog for the Edit Fields dialog on the Product Details page.
// Ported verbatim from the HTML mockup `adam_newest_v3 1.html` lines 9855–9903
// (BULK_EDIT_FIELDS).

export type EditFieldType = "text" | "select" | "date" | "dollar" | "agencyLocation";

// Identifies a set of select options that must be fetched from an API at
// runtime (resolved by the modal via a hook) instead of being hardcoded here.
export type EditFieldOptionsSource =
  | "propertyType"
  | "recordType"
  | "policyType"
  | "state";

export interface EditField {
  key: string;
  label: string;
  group: string;
  type: EditFieldType;
  options?: string[];
  // When set, the modal supplies the select options from the matching API
  // hook instead of the static `options` array above.
  optionsSource?: EditFieldOptionsSource;
  placeholder?: string;
  apiKey?: string; // Optional key to use when sending the field value to the API (if different from `key`).
}

export const EDIT_FIELDS: EditField[] = [
  // Policy Identity
  {
    key: "state",
    label: "State",
    group: "Policy Identity",
    type: "select",
    // Options come from /lookup/GetStateList (see useStateList).
    optionsSource: "state",
    apiKey: "StateCode"
  },
  {
    key: "countyCode",
    label: "County",
    group: "Policy Identity",
    type: "text",
    placeholder: "e.g. 003 - Hartford",
    apiKey: "CountyCode"
  },
  {
    key: "policyType",
    label: "Policy Type",
    group: "Policy Identity",
    type: "select",
    // Options come from /lookup/GetFormPrefixList (see usePolicyTypeOptions).
    optionsSource: "policyType",
    apiKey: "ProductTypeAbbrTypeCode"
  },
  { key: "policyFormName", label: "Policy Form Name", group: "Policy Identity", type: "text", apiKey: "PolicyFormName" },
  { key: "agencyLocation", label: "Agency Location", group: "Policy Identity", type: "agencyLocation" },
  { key: "municipality", label: "Municipality", group: "Policy Identity", type: "text", apiKey: "MunicipalityNum" },

  // Date Fields
  { key: "policyDate", label: "Effective Date", group: "Date Fields", type: "date", apiKey: "EffectiveDate"},
  { key: "issueDate", label: "Issue Date", group: "Date Fields", type: "date", apiKey: "IssueDate" },
  { key: "receivedDate", label: "Received Date", group: "Date Fields", type: "date", apiKey: "ReceivedDate" },
  { key: "voidedDate", label: "Voided Date", group: "Date Fields", type: "date", apiKey: "VoidedDate" },

  // Original Policy
  { key: "originalPolicy", label: "Original Policy Number", group: "Original Policy", type: "text", apiKey: "OriginalPolicyNumber" },
  { key: "originalPolicyDate", label: "Original Policy Date", group: "Original Policy", type: "date", apiKey: "OriginalPolicyDate" },

  // Transaction
  {
    key: "transCode",
    label: "Trans Code",
    group: "Transaction",
    type: "text",
    placeholder: "e.g. 3010",
    apiKey: "TransCode"
  },
  {
    key: "recordType",
    label: "Record Type",
    group: "Transaction",
    type: "select",
    // Options come from /lookup/GetPIListAsync/RecordType (see useRecordTypeOptions).
    optionsSource: "recordType",
    apiKey: "RecordTypeCode"
  },
  {
    key: "propertyType",
    label: "Property Type",
    group: "Transaction",
    type: "select",
    // Options come from /lookup/GetPIList/PropertyType (see usePropertyTypeOptions).
    optionsSource: "propertyType",
    apiKey: "PropertyTypeCode",
  },

  // Reported (financial — manual overrides; audited)
  // { key: "reportedLiability", label: "Reported Liability Amount", group: "Reported", type: "dollar" },
  // { key: "reportedPremium", label: "Reported Premium/Fee", group: "Reported", type: "dollar" },
  // { key: "reportedRetention", label: "Reported Underwriter Retention", group: "Reported", type: "dollar" },
  // { key: "reportedTax", label: "Reported Tax / State Fee", group: "Reported", type: "dollar" },

  // STRAFE-Calculated (financial — manual overrides; audited)
  { key: "liability", label: "Liability Amount", group: "FINANCIALS", type: "dollar", apiKey: "LiabilityAmount" },
  { key: "premium", label: "Premium/Fee", group: "FINANCIALS", type: "dollar", apiKey: "ActualFee"},
  { key: "retention", label: "Underwriter Retention", group: "FINANCIALS", type: "dollar", apiKey: "ActualRetention" },
  { key: "tax", label: "Tax / State Fee", group: "FINANCIALS", type: "dollar", apiKey: "ActualPremiumTax" },
  { key: "splitPercentage", label: "Split Percentage", group: "FINANCIALS", type: "dollar", apiKey: "SplitPercentage" },

  // Other liability tranches
  { key: "origPolicyAmt", label: "Original Policy Amount", group: "Other", type: "dollar", apiKey: "OriginalLiabilityAmount" },
  { key: "reissueLiab", label: "Reissue Liability", group: "Other", type: "dollar", apiKey: "ReissueLiabilityAmount"},
  { key: "siLiab", label: "SI Liability", group: "Other", type: "dollar", apiKey: "SILiabilityAmount" },
  { key: "addlLiab", label: "Additional Liability", group: "Other", type: "dollar", apiKey: "AdditionalLiabilityAmount"},
];

export const EDIT_FIELD_BY_KEY: Record<string, EditField> = Object.fromEntries(
  EDIT_FIELDS.map((f) => [f.key, f])
);

// Group order preserved from the catalog (insertion order of first occurrence).
export const EDIT_FIELD_GROUPS: string[] = Array.from(
  new Set(EDIT_FIELDS.map((f) => f.group))
);

export const EDIT_FIELDS_BY_GROUP: Record<string, EditField[]> = EDIT_FIELD_GROUPS.reduce(
  (acc, group) => {
    acc[group] = EDIT_FIELDS.filter((f) => f.group === group);
    return acc;
  },
  {} as Record<string, EditField[]>
);

// yyyy-mm-dd for native <input type="date">; the API returns full ISO
// timestamps (or nothing), so this just takes the date portion.
const toDateInputValue = (value?: string | null): string =>
  value ? value.slice(0, 10) : "";

// Pre-fills a single-record Edit dialog with the record's current,
// submit-compatible value for a field (select fields get the option code,
// not its display label; dates get yyyy-mm-dd). Complements
// FIELD_KEY_TO_RECORD_PROP in EditFieldsModal.tsx, which maps to a
// display-only value for that modal's before/after preview instead.
export function initialValueForField(
  record: Record<string, unknown>,
  fieldKey: string
): string {
  const get = (prop: string): string => {
    const v = record[prop];
    return v === null || v === undefined ? "" : String(v);
  };
  switch (fieldKey) {
    case "state":
      return get("StateCode");
    case "countyCode":
      return get("CountyCode");
    case "policyType":
      return get("ProductTypeAbbrTypeCode");
    case "policyFormName":
      return get("PolicyFormName");
    case "municipality":
      return get("MunicipalityNum");
    case "policyDate":
      return toDateInputValue(record.EffectiveDate as string | null);
    case "issueDate":
      return toDateInputValue(record.IssueDate as string | null);
    case "receivedDate":
      return toDateInputValue(record.ReceivedDate as string | null);
    case "voidedDate":
      return toDateInputValue(record.VoidedDate as string | null);
    case "originalPolicy":
      return get("OriginalPolicyNumber");
    case "originalPolicyDate":
      return toDateInputValue(record.OriginalPolicyDate as string | null);
    case "transCode":
      return get("TransCode");
    case "recordType":
      return get("RecordTypeCode");
    case "propertyType":
      return get("PropertyTypeCode");
    case "liability":
      return get("LiabilityAmount");
    case "premium":
      return get("ActualFee");
    case "tax":
      return get("ActualPremiumTax");
    case "splitPercentage":
      return get("NewRemitSplitPct");
    case "reissueLiab":
      return get("ReissueLiabilityAmount");
    case "siLiab":
      return get("SILiabilityAmount");
    case "addlLiab":
      return get("AdditionalLiabilityAmount");
    default:
      return "";
  }
}
